import React, { useCallback, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import UsersDrawer from '../UsersDrawer';
import { useUserManagementContext } from '../../context/UserManagementContext';
import {
  useCredentials,
  useUpdateCredentials,
} from '../../../CredentialsCard/useCredentials';
import { CredentialsList } from '../CredentialsList';
import { CredentialsActions } from '../CredentialsActions';
import { useCredentialsState } from '../../hooks/useCredentialsState';
import { useProjectByEnvs } from '../../hooks/useProjectByEnvs';
import { useParams } from 'react-router';
import { USER_MANAGEMENT_CLIENT_CREDENTIALS_WRITE } from '../../constants';
import { getApiErrorMessage } from '../../../../shared/api/getApiErrorMessage';

export const UserCredentialsDrawer = () => {
  const { id: userId } = useParams();
  const { loggedInUserData } = useUserManagementContext();

  const userHasWritePermission = loggedInUserData?.attributes.includes(
    USER_MANAGEMENT_CLIENT_CREDENTIALS_WRITE,
  );
  const { data } = useCredentials(userId);
  const { isUserCredentialsDrawerOpen, setIsUserCredentialsDrawerOpen } =
    useUserManagementContext();
  const [triggerValidation, setTriggerValidation] = useState(false);
  const {
    state,
    resetState,
    addCredentials,
    handleDeleteNew,
    handleDeleteExisting,
    handleUpdateNew,
    handleUpdateExisting,
    validateCredentials,
    prepareDataForSave,
  } = useCredentialsState({ initialData: data });
  const { mutate: updateCredentials, isPending } = useUpdateCredentials(userId);
  const handleClose = useCallback(() => {
    resetState();
    setIsUserCredentialsDrawerOpen(false);
  }, [resetState, setIsUserCredentialsDrawerOpen]);

  const handleSave = useCallback(() => {
    setTriggerValidation(true);

    if (!validateCredentials()) {
      enqueueSnackbar('Please fill in all required fields', {
        variant: 'error',
      });
      return;
    }

    const credentials = prepareDataForSave();
    updateCredentials(credentials, {
      onSuccess: () => {
        setTriggerValidation(false);
        handleClose();
        enqueueSnackbar('Credentials saved successfully', {
          variant: 'success',
        });
      },
      onError: error => {
        // eslint-disable-next-line no-console
        console.error('Failed to save credentials:', error);
        enqueueSnackbar(getApiErrorMessage(error, 'Failed to save credentials'), {
          variant: 'error',
        });
      },
    });
  }, [validateCredentials, prepareDataForSave, handleClose, updateCredentials]);

  const { usedByProject, noMoreAvailableEnvs } = useProjectByEnvs({
    externalCredentials: data?.external,
    deletedCredentials: state.deletedCredentials,
    newCredentials: state.newCredentials,
  });

  return (
    <UsersDrawer
      id="user-credentials-drawer"
      title={
        userHasWritePermission
          ? 'Set/View Client Credentials'
          : 'View Client Credentials'
      }
      handleDrawerClose={handleClose}
      isOpen={isUserCredentialsDrawerOpen}
      confirmOnClose={state.hasChanges}
      confirmTitle="Are you sure you want to cancel?"
      confirmContent="All the details provided will be lost."
      confirmButtonText="Cancel Setting Credentials"
      cancelButtonText="Dismiss"
    >
      <CredentialsList
        dxmCredentials={data?.dxm[0]}
        externalCredentials={data?.external}
        newCredentials={state.newCredentials}
        deletedCredentials={state.deletedCredentials}
        shouldValidate={triggerValidation}
        onDeleteNew={handleDeleteNew}
        onDeleteExisting={handleDeleteExisting}
        onUpdateNew={handleUpdateNew}
        onUpdateExisting={handleUpdateExisting}
        hasWritePermission={userHasWritePermission}
        usedByProject={usedByProject}
      />
      {userHasWritePermission && (
        <CredentialsActions
          hasChanges={state.hasChanges}
          isLoading={isPending}
          onAdd={addCredentials}
          onSave={handleSave}
          noMoreAvailableEnvs={noMoreAvailableEnvs}
        />
      )}
    </UsersDrawer>
  );
};
